import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Search } from 'lucide-react';
import './BecomeContributor.scss';
import { publicRequest } from '../../requestMethods';
import MosqueLoader from '../../utils/MosqueLoader';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer';
import { LOCATIONS } from '../../utils/Locations';

const BecomeContributor = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        whatsapp: '',
        district: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [showThanks, setShowThanks] = useState(false);
    const [error, setError] = useState('');
    const [districtSearch, setDistrictSearch] = useState('');
    const [districtSuggestions, setDistrictSuggestions] = useState([]);
    const [showDistrictSuggestions, setShowDistrictSuggestions] = useState(false);
    const districtSearchRef = useRef(null);
    const districtSuggestionsRef = useRef(null);

    // Get all districts from LOCATIONS
    const allDistricts = Object.values(LOCATIONS).flatMap(location => location.districts);
    const sortedDistricts = [...allDistricts].sort();

    // Sync districtSearch with formData.district
    useEffect(() => {
        if (formData.district && !districtSearch) {
            setDistrictSearch(formData.district);
        }
    }, [formData.district]);

    // Handle district search
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                districtSuggestionsRef.current &&
                !districtSuggestionsRef.current.contains(event.target) &&
                districtSearchRef.current &&
                !districtSearchRef.current.contains(event.target)
            ) {
                setShowDistrictSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDistrictSearch = (value) => {
        setDistrictSearch(value);
        
        if (value.length < 2) {
            setDistrictSuggestions([]);
            setShowDistrictSuggestions(false);
            return;
        }

        const queryLower = value.toLowerCase();
        const filtered = sortedDistricts
            .filter(district => {
                const districtLower = district.toLowerCase();
                return districtLower.includes(queryLower) || districtLower.startsWith(queryLower);
            })
            .sort((a, b) => {
                const aLower = a.toLowerCase();
                const bLower = b.toLowerCase();
                const aStarts = aLower.startsWith(queryLower);
                const bStarts = bLower.startsWith(queryLower);
                
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;
                return aLower.localeCompare(bLower);
            })
            .slice(0, 8);

        setDistrictSuggestions(filtered);
        setShowDistrictSuggestions(filtered.length > 0);
    };

    const handleDistrictSelect = (district) => {
        setFormData(prev => ({
            ...prev,
            district: district
        }));
        setDistrictSearch(district);
        setShowDistrictSuggestions(false);
        // Clear error when district is selected
        if (error) setError('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validate district is selected
        if (!formData.district || formData.district.trim() === '') {
            setError('Please select a district from the suggestions');
            return;
        }
        
        setLoading(true);

        try {
            const response = await publicRequest.post("/utils/register-contributor", formData);
            
            if (response.data.success) {
                setShowThanks(true);
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    whatsapp: '',
                    district: ''
                });
                setDistrictSearch('');
            } else {
                setError(response.data.message || 'Registration failed. Please try again.');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError(
                error.response?.data?.message || 
                'Failed to register. Please check your connection and try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCloseThanks = () => {
        setShowThanks(false);
        navigate('/');
    };

    return (
        <div className="become-contributor">
            <Navbar />
            {loading && <MosqueLoader />}
            <div className="become-contributor__container">
                <div className="become-contributor__header">
                    <h1 className="become-contributor__title">Become a Contributor</h1>
                    <p className="become-contributor__subtitle">
                        Join us as a <span className="become-contributor__arabic">مساهم</span> (Musahim) and help build our community
                    </p>
                </div>
                    
                    {showThanks ? (
                        <div className="become-contributor__thanks">
                            <CheckCircle size={64} className="become-contributor__thanks-icon" />
                            <h2 className="become-contributor__thanks-title">Thank You!</h2>
                            <p className="become-contributor__thanks-message">
                                Your registration has been received. We'll be in touch soon to welcome you to our community!
                            </p>
                            <button 
                                className="become-contributor__thanks-button"
                                onClick={handleCloseThanks}
                            >
                                Return to Home
                            </button>
                        </div>
                    ) : (
                        <form className="contributor-form" onSubmit={handleSubmit}>
                            {error && (
                                <div className="contributor-form__error">
                                    {error}
                                </div>
                            )}
                            
                            <div className="contributor-form__group">
                                <label htmlFor="name">Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your full name"
                                />
                            </div>

                            <div className="contributor-form__group">
                                <label htmlFor="email">Email *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your email address"
                                />
                            </div>

                            <div className="contributor-form__group">
                                <label htmlFor="phone">Phone Number *</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your phone number"
                                />
                            </div>

                            <div className="contributor-form__group">
                                <label htmlFor="whatsapp">WhatsApp Number *</label>
                                <input
                                    type="tel"
                                    id="whatsapp"
                                    name="whatsapp"
                                    value={formData.whatsapp}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your WhatsApp number"
                                />
                            </div>

                            <div className="contributor-form__group contributor-form__group--district">
                                <label htmlFor="district">District *</label>
                                <div className="contributor-form__district-search" ref={districtSearchRef}>
                                    <Search className="contributor-form__district-search-icon" size={18} />
                                    <input
                                        type="text"
                                        id="district"
                                        name="district"
                                        value={districtSearch}
                                        onChange={(e) => handleDistrictSearch(e.target.value)}
                                        onFocus={() => {
                                            if (districtSuggestions.length > 0) {
                                                setShowDistrictSuggestions(true);
                                            }
                                        }}
                                        placeholder="Search your district..."
                                    />
                                    {showDistrictSuggestions && districtSuggestions.length > 0 && (
                                        <div className="contributor-form__district-suggestions" ref={districtSuggestionsRef}>
                                            {districtSuggestions.map((district, index) => (
                                                <div
                                                    key={district}
                                                    className="contributor-form__district-suggestion-item"
                                                    onClick={() => handleDistrictSelect(district)}
                                                >
                                                    {district}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                className="contributor-form__submit"
                                disabled={loading}
                            >
                                {loading ? 'Registering...' : 'Register as Contributor'}
                            </button>
                        </form>
                    )}
            </div>
            <Footer />
        </div>
    );
};

export default BecomeContributor;
